import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { useState, useEffect, useId } from "react";
import type { CreateCategoryAttributeRequest } from "@/services/api";
import {
  useAttributeValues,
  AttributeValuesSection,
  prepareAttributeValues,
  validateAttributeData,
} from "./shared";

interface AttributeCreateDialogProps {
  open: boolean;
  isLoading?: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateCategoryAttributeRequest) => void;
}

export function AttributeCreateDialog({
  open,
  isLoading,
  onOpenChange,
  onSubmit,
}: AttributeCreateDialogProps) {
  const [name, setName] = useState("");
  const {
    values,
    setValues,
    draggedIndex,
    dragOverIndex,
    handleAddValue,
    handleRemoveValue,
    handleValueChange,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    resetDragState,
  } = useAttributeValues([{ id: "1", value: "", order: 1 }]);

  useEffect(() => {
    if (open) {
      setName("");
      setValues([{ id: "1", value: "", order: 1 }]);
      resetDragState();
    }
  }, [open, setValues, resetDragState]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateAttributeData(name, values)) return;

    const validValues = prepareAttributeValues(values);
    if (validValues.length === 0) return;

    onSubmit({
      name: name.trim(),
      values: validValues,
    });
  };

  const hasValidData = validateAttributeData(name, values);
  const descriptionId = useId();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto"
        aria-describedby={descriptionId}
      >
        <DialogHeader>
          <DialogTitle>Create Attribute</DialogTitle>
          <DialogDescription>
            Create a new attribute with multiple values.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium text-ink-800">Name</label>
            <Input
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Enter attribute name"
            />
          </div>

          <AttributeValuesSection
            values={values}
            draggedIndex={draggedIndex}
            dragOverIndex={dragOverIndex}
            onValueChange={handleValueChange}
            onRemove={handleRemoveValue}
            onAdd={handleAddValue}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
          />

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !hasValidData}>
              Create Attribute
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
